/**
 * G06. A* Pathfinding — easystarjs wrapper for enemy navigation around obstacles.
 *
 * Spec: docs/GAME_TECHNIQUES_WIKI.md G06. Drop-in NavigationAgent2D analogue for
 * Phaser. Enemies that "chase" the player should request a path every ~0.5s
 * and step along the returned tile centres rather than the straight line —
 * that way they walk around bosses / barriers instead of grinding the wall.
 *
 * Design rules:
 * - Phaser-free. Pure TypeScript wrapper around easystarjs so tests can run in
 *   node without a Phaser scene (see tests/game/ai/pathfinder.test.ts).
 * - World pixels in / out. `findPath` takes start/end in pixels; callback gets
 *   back a path in pixels (tile centres). Internal grid maths is hidden.
 * - update() MUST be called each frame to drive easystarjs's async calculate
 *   pump. We do NOT enableSync — keeping work spread across frames matters at
 *   60FPS once we have 20+ grunts (G06 acceptance: 0.5s repath × N grunts).
 * - TileMap fallback: when the battle scene has no TileMap (current
 *   horizontal-battle-scene only uses a tileSprite background), callers build
 *   the walkable grid from axis-aligned bounding boxes via
 *   `buildGridFromObstacles`. This is the explicit退化 path called out in the
 *   dispatch — don't硬幹 if TileMap is missing.
 */

import EasyStar from 'easystarjs';

/** Walkable tile marker. Anything else (including obstacle id 1) is blocked. */
export const WALKABLE = 0;
/** Blocked tile marker used by buildGridFromObstacles. */
export const BLOCKED = 1;

/** Axis-aligned rectangle in world (pixel) space, used by the fallback builder. */
export interface ObstacleRect {
  /** Left edge in world px. */
  x: number;
  /** Top edge in world px. */
  y: number;
  /** Width in world px. */
  width: number;
  /** Height in world px. */
  height: number;
}

/** Result emitted to findPath callback. `null` means no path exists. */
export type PathResult = Array<{ x: number; y: number }> | null;

export interface PathfinderOptions {
  /** Width of one grid cell in world px. Pick a value ≥ 1.5× enemy size to
   *  keep diagonals from clipping corners (G06 acceptance: 繞 1-2 障礙物). */
  cellSize: number;
  /** Total walkable area width in cells (gridWidth = ceil(worldW / cellSize)). */
  gridWidth: number;
  /** Total walkable area height in cells. */
  gridHeight: number;
  /** Pre-built 2D walkable grid (gridHeight rows × gridWidth cols). Each cell
   *  is either `WALKABLE` (0) or `BLOCKED` (1). When omitted, the grid starts
   *  fully open and callers should use `setObstacles`. */
  grid?: number[][];
  /** Whether to allow diagonal moves. Defaults to true — looks more natural. */
  enableDiagonals?: boolean;
  /** Whether diagonals can squeeze through 1-cell-wide gaps. Defaults to false
   *  so enemies don't slip through corners of obstacles. */
  allowCornerCutting?: boolean;
  /** When true, findPath callbacks fire synchronously inside calculate() — no
   *  setTimeout deferral. Defaults to true: on small grids (≤ ~30×20 cells, our
   *  battle scene size) sync mode is fast enough and avoids a microtask lag
   *  between "find" and "follow". Set false only if you have a huge grid and
   *  want to spread work across frames. */
  enableSync?: boolean;
}

/**
 * Build a walkable grid from a list of axis-aligned obstacle rectangles in
 * world pixels. Used as the TileMap-less fallback for scenes that only have
 * sprite obstacles (e.g. horizontal-battle-scene: boss bbox treated as obstacle).
 *
 * Any grid cell whose centre lies inside any obstacle rect is marked BLOCKED;
 * everything else is WALKABLE.
 */
export function buildGridFromObstacles(
  worldWidth: number,
  worldHeight: number,
  cellSize: number,
  obstacles: ObstacleRect[],
): number[][] {
  if (cellSize <= 0) throw new Error('cellSize must be positive');
  const cols = Math.max(1, Math.ceil(worldWidth / cellSize));
  const rows = Math.max(1, Math.ceil(worldHeight / cellSize));
  const grid: number[][] = [];
  for (let row = 0; row < rows; row++) {
    const cy = (row + 0.5) * cellSize;
    const line: number[] = [];
    for (let col = 0; col < cols; col++) {
      const cx = (col + 0.5) * cellSize;
      let blocked = false;
      for (const o of obstacles) {
        if (cx >= o.x && cx < o.x + o.width && cy >= o.y && cy < o.y + o.height) {
          blocked = true;
          break;
        }
      }
      line.push(blocked ? BLOCKED : WALKABLE);
    }
    grid.push(line);
  }
  return grid;
}

/** Internal helper: convert world-px to grid cell coords, clamped to bounds. */
function worldToGrid(x: number, y: number, cellSize: number, cols: number, rows: number): { gx: number; gy: number } {
  const gx = Math.max(0, Math.min(cols - 1, Math.floor(x / cellSize)));
  const gy = Math.max(0, Math.min(rows - 1, Math.floor(y / cellSize)));
  return { gx, gy };
}

/** Internal helper: convert grid cell centre to world px. */
function gridToWorld(gx: number, gy: number, cellSize: number): { x: number; y: number } {
  return { x: (gx + 0.5) * cellSize, y: (gy + 0.5) * cellSize };
}

/**
 * Pathfinder wraps an easystarjs instance with world-pixel I/O and a frame-
 * driven update pump. One instance per scene; share across all enemies that
 * need pathfinding to amortise grid setup.
 */
export class Pathfinder {
  private readonly easystar: EasyStar.js;
  private readonly cellSize: number;
  private readonly cols: number;
  private readonly rows: number;
  private grid: number[][];

  constructor(options: PathfinderOptions) {
    if (options.cellSize <= 0) throw new Error('cellSize must be positive');
    if (options.gridWidth <= 0 || options.gridHeight <= 0) {
      throw new Error('gridWidth and gridHeight must be positive');
    }
    this.cellSize = options.cellSize;
    this.cols = options.gridWidth;
    this.rows = options.gridHeight;
    this.grid =
      options.grid ??
      Array.from({ length: this.rows }, () => Array<number>(this.cols).fill(WALKABLE));

    this.easystar = new EasyStar.js();
    this.easystar.setGrid(this.grid);
    this.easystar.setAcceptableTiles([WALKABLE]);

    if (options.enableDiagonals !== false) this.easystar.enableDiagonals();
    if (options.allowCornerCutting) this.easystar.enableCornerCutting();
    else this.easystar.disableCornerCutting();
    if (options.enableSync !== false) this.easystar.enableSync();
  }

  /** Replace the walkable grid wholesale (e.g. after rebuilding from new obstacles). */
  setGrid(grid: number[][]): void {
    if (grid.length !== this.rows) throw new Error('grid row count mismatch');
    if (grid[0]?.length !== this.cols) throw new Error('grid col count mismatch');
    this.grid = grid;
    this.easystar.setGrid(grid);
  }

  /** Rebuild walkable grid from a fresh obstacle list (TileMap-less fallback). */
  setObstacles(worldWidth: number, worldHeight: number, obstacles: ObstacleRect[]): void {
    const grid = buildGridFromObstacles(worldWidth, worldHeight, this.cellSize, obstacles);
    this.setGrid(grid);
  }

  /** Expose the current grid for tests / debug overlays. Read-only copy not enforced for perf. */
  getGrid(): number[][] {
    return this.grid;
  }

  /** Cell size in world px (read-only). */
  getCellSize(): number {
    return this.cellSize;
  }

  /**
   * Request a path from `start` to `end` (world pixels). Callback fires once
   * easystarjs finishes — typically within a frame or two of `update()`. The
   * callback receives world-pixel tile-centre points, or `null` if no path
   * exists (e.g. target inside an obstacle).
   *
   * Returns the easystar instance id, which callers can pass to
   * `cancelPath` if a fresh request supersedes it (e.g. enemy retargets).
   */
  findPath(
    start: { x: number; y: number },
    end: { x: number; y: number },
    callback: (path: PathResult) => void,
  ): number {
    const s = worldToGrid(start.x, start.y, this.cellSize, this.cols, this.rows);
    const e = worldToGrid(end.x, end.y, this.cellSize, this.cols, this.rows);
    return this.easystar.findPath(s.gx, s.gy, e.gx, e.gy, (gridPath) => {
      if (gridPath === null) {
        callback(null);
        return;
      }
      const worldPath = gridPath.map((p) => gridToWorld(p.x, p.y, this.cellSize));
      callback(worldPath);
    });
  }

  /** Cancel a pending findPath request. Returns true if it was found and cancelled. */
  cancelPath(instanceId: number): boolean {
    return this.easystar.cancelPath(instanceId);
  }

  /**
   * Drive one step of easystarjs's async A* search. Call once per frame from
   * the owning scene's update(). easystarjs spreads search iterations across
   * frames to avoid main-thread spikes; without `update()`, callbacks never
   * fire.
   */
  update(): void {
    this.easystar.calculate();
  }
}

/**
 * Tiny helper for enemies: advance one step toward the next path waypoint.
 * Returns the post-step position plus the (possibly trimmed) remaining path
 * — when the entity is within `arriveRadius` of the next waypoint, that
 * waypoint is popped so the caller naturally walks the path one node at a time.
 *
 * Phaser-free. Pure maths. Speed is in px/sec, dt in seconds.
 */
export function followPathStep(
  position: { x: number; y: number },
  path: Array<{ x: number; y: number }>,
  speedPxPerSec: number,
  dtSec: number,
  arriveRadius: number = 4,
): { x: number; y: number; remaining: Array<{ x: number; y: number }> } {
  if (path.length === 0) return { x: position.x, y: position.y, remaining: [] };
  const target = path[0];
  const dx = target.x - position.x;
  const dy = target.y - position.y;
  const dist = Math.hypot(dx, dy);
  if (dist <= arriveRadius) {
    // Reached this waypoint — pop it and recurse with the rest of the path so
    // a single tick can chew through several tightly-packed waypoints when dt
    // is large (tab refocus etc.). Bounded by path.length so we can't loop.
    return followPathStep(position, path.slice(1), speedPxPerSec, dtSec, arriveRadius);
  }
  const step = speedPxPerSec * dtSec;
  if (step >= dist) {
    // Overshoots target this frame — snap to target, pop waypoint, keep moving.
    const remaining = path.slice(1);
    return followPathStep(target, remaining, speedPxPerSec, dtSec - dist / speedPxPerSec, arriveRadius);
  }
  const nx = position.x + (dx / dist) * step;
  const ny = position.y + (dy / dist) * step;
  return { x: nx, y: ny, remaining: path };
}
